import React, { useState, useEffect, useCallback } from 'react';
import Component from '../../components/Component';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Chip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import {
  listUserCredits,
  getUserCredits,
  setUserCredits,
  addUserCredits,
} from '../../services/aiChatCreditsApi';

const PAGE_SIZE = 50;

function AiChatCredits() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchUserId, setSearchUserId] = useState('');
  const [searchResult, setSearchResult] = useState(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); // 'add' | 'set'
  const [dialogUserId, setDialogUserId] = useState('');
  const [dialogAmount, setDialogAmount] = useState('');
  const [dialogLoading, setDialogLoading] = useState(false);

  // Grant-to-new-user state
  const [newUserId, setNewUserId] = useState('');
  const [newUserAmount, setNewUserAmount] = useState('');
  const [grantLoading, setGrantLoading] = useState(false);

  const fetchUsers = useCallback(async (pageOverride) => {
    setLoading(true);
    setError('');
    const currentPage = pageOverride !== undefined ? pageOverride : page;
    try {
      const res = await listUserCredits(PAGE_SIZE, currentPage * PAGE_SIZE);
      setUsers(res.data?.users || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
  };

  // Search for a specific user's credits
  const handleSearch = async () => {
    if (!searchUserId.trim()) {
      setSearchResult(null);
      return;
    }
    setError('');
    try {
      const res = await getUserCredits(searchUserId.trim());
      setSearchResult(res.data);
    } catch (err) {
      setError(err.message || 'User not found');
      setSearchResult(null);
    }
  };

  // Open dialog to edit user credits
  const openDialog = (mode, userId, currentCredits = 0) => {
    setDialogMode(mode);
    setDialogUserId(userId);
    setDialogAmount(mode === 'set' ? String(currentCredits) : '');
    setError('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setDialogUserId('');
    setDialogAmount('');
  };

  const handleDialogSubmit = async () => {
    const amount = Number(dialogAmount);
    if (!Number.isFinite(amount) || (dialogMode === 'add' ? amount <= 0 : amount < 0)) {
      setError('Invalid amount');
      return;
    }
    setDialogLoading(true);
    try {
      if (dialogMode === 'add') {
        await addUserCredits(dialogUserId, amount);
        setSuccess(`Added ${amount} credits to user ${dialogUserId}`);
      } else {
        await setUserCredits(dialogUserId, amount);
        setSuccess(`Set user ${dialogUserId} balance to ${amount}`);
      }
      closeDialog();
      // Refresh list if the updated user is visible, and re-search
      fetchUsers();
      if (searchResult && String(searchResult.user_id) === String(dialogUserId)) {
        handleSearch();
      }
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message || 'Failed to update credits');
    } finally {
      setDialogLoading(false);
    }
  };

  // Grant credits to a new user (not yet in the list)
  const handleGrantToNewUser = async () => {
    const amount = Number(newUserAmount);
    if (!newUserId.trim() || !Number.isFinite(amount) || amount <= 0) {
      setError('Enter a valid user ID and positive amount');
      return;
    }
    setGrantLoading(true);
    setError('');
    try {
      await addUserCredits(newUserId.trim(), amount);
      setSuccess(`Granted ${amount} credits to user ${newUserId.trim()}`);
      setNewUserId('');
      setNewUserAmount('');
      fetchUsers();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message || 'Failed to grant credits');
    } finally {
      setGrantLoading(false);
    }
  };

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <h3>AI Chat Credits</h3>
                <p>Manage user credit balances for Duggy AI. 1 credit = 1 message. 100 fan energy = 10 credits.</p>
              </div>
            </div>
          </div>

          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

          <Grid container spacing={2}>
            {/* Grant credits to a user */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Grant Credits</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Add credits to any user (by user ID or UUID). Creates a credit record if none exists.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <TextField
                      label="User ID / UUID"
                      size="small"
                      value={newUserId}
                      onChange={(e) => setNewUserId(e.target.value)}
                      sx={{ flex: 2, minWidth: 200 }}
                    />
                    <TextField
                      label="Amount"
                      size="small"
                      type="number"
                      value={newUserAmount}
                      onChange={(e) => setNewUserAmount(e.target.value)}
                      sx={{ flex: 1, minWidth: 100 }}
                      inputProps={{ min: 1 }}
                    />
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleGrantToNewUser}
                      disabled={grantLoading}
                    >
                      {grantLoading ? <CircularProgress size={18} /> : 'Grant'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Search a specific user */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Look Up User</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Search for a specific user by ID or UUID to see their balance and stats.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      label="User ID / UUID"
                      size="small"
                      fullWidth
                      value={searchUserId}
                      onChange={(e) => setSearchUserId(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <Button variant="outlined" startIcon={<SearchIcon />} onClick={handleSearch}>
                      Search
                    </Button>
                  </Box>
                  {searchResult && (
                    <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
                      <Typography variant="body2"><strong>User:</strong> {searchResult.user_id}</Typography>
                      <Typography variant="body2">
                        <strong>Credits:</strong> <Chip label={searchResult.credits || 0} color="primary" size="small" />
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Used: {searchResult.total_used || 0} · Granted: {searchResult.total_granted || 0} · Purchased: {searchResult.total_purchased || 0}
                      </Typography>
                      <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                        <Button size="small" variant="outlined" onClick={() => openDialog('add', searchResult.user_id, searchResult.credits)}>
                          Add Credits
                        </Button>
                        <Button size="small" variant="outlined" onClick={() => openDialog('set', searchResult.user_id, searchResult.credits)}>
                          Set Balance
                        </Button>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* All users table */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">All Users with Credits ({total})</Typography>
                    <IconButton onClick={() => fetchUsers()} disabled={loading}>
                      <RefreshIcon />
                    </IconButton>
                  </Box>

                  {loading ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : users.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                      No users with credit records yet.
                    </Typography>
                  ) : (
                    <>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>User ID</TableCell>
                              <TableCell align="right">Credits</TableCell>
                              <TableCell align="right">Used</TableCell>
                              <TableCell align="right">Granted</TableCell>
                              <TableCell align="right">Purchased</TableCell>
                              <TableCell>Updated By</TableCell>
                              <TableCell>Updated At</TableCell>
                              <TableCell align="center">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {users.map((u) => (
                              <TableRow key={u.user_id}>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                    {u.user_id}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Chip
                                    label={u.credits || 0}
                                    color={u.credits > 0 ? 'primary' : 'default'}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell align="right">{u.total_used || 0}</TableCell>
                                <TableCell align="right">{u.total_granted || 0}</TableCell>
                                <TableCell align="right">{u.total_purchased || 0}</TableCell>
                                <TableCell>
                                  <Typography variant="caption">{u.updated_by || '—'}</Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="caption">
                                    {u.updated_at ? new Date(u.updated_at).toLocaleString() : '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <IconButton size="small" onClick={() => openDialog('add', u.user_id, u.credits)} title="Add credits">
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton size="small" onClick={() => openDialog('set', u.user_id, u.credits)} title="Set balance">
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      {total > PAGE_SIZE && (
                        <TablePagination
                          component="div"
                          count={total}
                          page={page}
                          onPageChange={handlePageChange}
                          rowsPerPage={PAGE_SIZE}
                          rowsPerPageOptions={[PAGE_SIZE]}
                        />
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </div>
      </div>

      {/* Add / Set credits dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {dialogMode === 'add' ? 'Add Credits' : 'Set Credit Balance'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            User: <code>{dialogUserId}</code>
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="number"
            label={dialogMode === 'add' ? 'Amount to add' : 'New balance'}
            value={dialogAmount}
            onChange={(e) => setDialogAmount(e.target.value)}
            inputProps={{ min: dialogMode === 'add' ? 1 : 0 }}
            helperText={
              dialogMode === 'add'
                ? 'Will be added to the current balance'
                : 'Overwrites current balance'
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={dialogLoading}>Cancel</Button>
          <Button onClick={handleDialogSubmit} variant="contained" disabled={dialogLoading}>
            {dialogLoading ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Component>
  );
}

export default AiChatCredits;
